/**
 * The CANONICAL English catalog (Phase 3) — aligned to Agent 5's published
 * `VIEW_MESSAGE_KEYS`. This is THE English catalog: exported as `EN_CATALOG`, shipped on
 * every host through `host-bundle`, and enforced for parity by `coverage.test.ts`.
 *
 * It covers the 59 canonical structural keys the L4 builder emits (`view/messages.ts`) plus
 * the §9.1 domain-object labels (claim/constraint/edge), so the real view-model renders
 * end-to-end with zero `«id»` fallbacks. Every value is a whole-message ICU pattern.
 *
 * W/M grades use the `::numbering-system/latn` skeleton so the technical grade token stays
 * Latin in every locale (it is a stable id, not prose); the renderer LTR-isolates it.
 *
 * The pre-Agent-5 Phase-1 vocabulary (`catalog-en.ts` / `message-ids.ts`) is retired from
 * this canonical surface; it survives only as the internal `EN_LEGACY_CATALOG` test fixture.
 */
import { P2 } from '../message-ids-phase2.js';
export const EN_CATALOG = {
    locale: 'en',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Inquiry',
        [P2.sectionClaims]: 'Claims',
        [P2.sectionIntegrity]: 'Integrity',
        [P2.axisWarrantLabel]: 'Warrant',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirical',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definition} interp {Interpretation} testable {Testable} tested {Tested} refuted {Refuted} program {Research programme} philos {Philosophical} other {Unknown}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Strength level {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Explorable',
        [P2.stateRefuted]: 'Refuted',
        [P2.stateOutside]: 'Outside scope',
        [P2.verdictConflict]: 'Conflicts with {constraints}',
        [P2.verdictOutside]: 'Needs {missing}',
        [P2.lossRegisterPlain]: 'Simplified to plain language — some precision is left out.',
        [P2.lossRegisterKid]: 'Simplified for younger readers — many details are left out.',
        [P2.lossProfileDetail]: 'The full Warrant / Empirical / Modal profile is summarized here.',
        [P2.lossConflictDetail]: 'The conflict details are shortened in this view.',
        [P2.laneVerified]: 'Verified',
        [P2.laneUnverified]: 'Unverified',
        [P2.laneConflict]: 'Conflict proven',
        [P2.constraintLogic]: 'Logic',
        [P2.constraintLightBends]: 'Light bends in a gravitational field',
        [P2.constraintParallelPostulate]: 'The parallel postulate',
        [P2.claimScalarGravity]: 'Scalar theory of gravity',
        [P2.claimNonEuclidean]: 'Non-Euclidean geometry',
        [P2.claimSubstrateSr]: 'Substrate interpretation of special relativity',
        [P2.edgeExtObservation]: 'External observation',
        [P2.edgeResourceSample]: 'Shared computational resource',
        [P2.edgeHeuristicSample]: 'Heuristic guidance',
        [P2.edgeSharedSample]: 'Shared foundation',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Constraints',
        [P2.sectionEdges]: 'Edges',
        [P2.sectionFrame]: 'Frame',
        [P2.sectionExposure]: 'Exposure',
        [P2.statusAdoptedFormal]: 'Adopted (formal)',
        [P2.statusAdoptedInterpretive]: 'Adopted (interpretive)',
        [P2.statusSuspended]: 'Suspended',
        [P2.edgeEffectConstraint]: 'Composes a constraint (can refute)',
        [P2.edgeEffectResource]: 'Resource — no effect on verdicts',
        [P2.edgeEffectHeuristic]: 'Heuristic — no effect on verdicts',
        [P2.edgeEffectShared]: 'Shared foundation — no effect on verdicts',
        [P2.frameActive]: '{count, plural, =0 {No active constraints} one {# active constraint} other {# active constraints}}',
        [P2.exposureNone]: 'No interpretive dependencies',
        [P2.exposureDep]: 'Depends on {id}',
        [P2.laneTrackMachine]: 'Machine',
        [P2.laneTrackExpert]: 'Expert',
        [P2.laneTrackCommunity]: 'Community',
        [P2.expertAudited]: 'Audited',
        [P2.expertInProgress]: 'In progress',
        [P2.expertNotReviewed]: 'Not reviewed',
        [P2.communityFlags]: 'Flags exist',
        [P2.communityNone]: 'No flags',
        [P2.controlChallenge]: 'Challenge',
        [P2.controlActivate]: 'Activate',
        [P2.controlMakeInterpretive]: 'Make interpretive',
        [P2.controlImportEdge]: 'Import edge',
        [P2.controlPromote]: 'Promote',
        [P2.controlUndo]: 'Undo',
        [P2.controlRedo]: 'Redo',
        [P2.controlRegister]: 'Register',
        [P2.promoteRejected]: 'Promotion rejected',
        [P2.promoteAccepted]: 'Promotion accepted',
        [P2.promoteReasonRefutationTerminal]: 'A proof cannot cure a refutation.',
        [P2.promoteReasonKeystone]: 'A keystone promotion needs empirical support.',
        [P2.promoteReasonEmpiricalNotFixed]: 'The empirical kind is not fixed.',
        [P2.promoteReasonModalNotFixed]: 'The modal grade is not fixed.',
        [P2.promoteReasonWarrantNotImproved]: 'The warrant grade is not improved.',
        // --- compute integration ---
        [P2.evidenceComputation]: '{engine} computed {op} = {result} under {config}',
        [P2.lossNumericRounding]: 'Result rounded to {dp} decimal places',
    },
};
/**
 * @deprecated Back-compat alias for the canonical {@link EN_CATALOG}. Kept so existing
 * importers (e.g. `host-bundle`) that reference `EN_PHASE2_CATALOG` keep working. New code
 * should import `EN_CATALOG`.
 */
export const EN_PHASE2_CATALOG = EN_CATALOG;
//# sourceMappingURL=en.js.map